import { useParams, useLocation } from "react-router-dom";
import Logo from "../../components/Logo";

const AGBAZA_GREEN = "#00553E";

type DocType = "consultation" | "ordonnance";

const DOC_META: Record<DocType, { label: string; description: string }> = {
  consultation: {
    label: "Fiche de consultation médicale",
    description:
      "Ce document a été généré et signé électroniquement par le système AGBAZA à la suite d'une consultation médicale enregistrée sur la plateforme.",
  },
  ordonnance: {
    label: "Ordonnance médicale",
    description:
      "Cette ordonnance a été émise et certifiée électroniquement par le système AGBAZA par un professionnel de santé enregistré sur la plateforme.",
  },
};

function getDocType(pathname: string): DocType {
  if (pathname.startsWith("/ordonnance")) return "ordonnance";
  return "consultation";
}

export default function DocumentVerificationPage() {
  const { id } = useParams<{ id: string }>();
  const { pathname } = useLocation();
  const docType = getDocType(pathname);
  const meta = DOC_META[docType];

  return (
    <div
      data-theme="previn"
      className="min-h-screen bg-base-100 flex flex-col items-center justify-center px-4 py-12"
    >
      <div className="w-full max-w-lg flex flex-col items-center gap-8">

        {/* Logo */}
        <Logo size={72} />

        {/* Carte de vérification */}
        <div className="w-full rounded-2xl border border-base-300 bg-base-200 shadow-sm overflow-hidden">

          {/* Bandeau vert */}
          <div
            className="flex items-center gap-3 px-6 py-4"
            style={{ backgroundColor: AGBAZA_GREEN }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="white"
              className="w-7 h-7 shrink-0"
            >
              <path
                fillRule="evenodd"
                d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm13.36-1.814a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z"
                clipRule="evenodd"
              />
            </svg>
            <div>
              <p className="text-white font-bold text-base leading-tight">
                Document vérifié
              </p>
              <p className="text-white/80 text-sm leading-tight">
                Conforme au système AGBAZA
              </p>
            </div>
          </div>

          {/* Corps */}
          <div className="px-6 py-6 flex flex-col gap-5">

            {/* Type de document */}
            <div className="flex flex-col gap-1">
              <span className="text-xs font-semibold uppercase tracking-widest opacity-50">
                Type de document
              </span>
              <span className="text-base font-semibold text-base-content">
                {meta.label}
              </span>
            </div>

            {/* Identifiant */}
            <div className="flex flex-col gap-1">
              <span className="text-xs font-semibold uppercase tracking-widest opacity-50">
                Identifiant du document
              </span>
              <code
                className="text-sm font-mono px-3 py-2 rounded-lg bg-base-300 text-base-content break-all"
              >
                {id ?? "—"}
              </code>
            </div>

            {/* Description */}
            <p className="text-sm text-base-content/70 leading-relaxed">
              {meta.description}
            </p>

            {/* Séparateur */}
            <div className="border-t border-base-300" />

            {/* Mention légale */}
            <div className="flex items-start gap-3">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="w-5 h-5 shrink-0 opacity-40 mt-0.5"
              >
                <path
                  fillRule="evenodd"
                  d="M12 1.5a5.25 5.25 0 0 0-5.25 5.25v3a3 3 0 0 0-3 3v6.75a3 3 0 0 0 3 3h10.5a3 3 0 0 0 3-3v-6.75a3 3 0 0 0-3-3v-3c0-2.9-2.35-5.25-5.25-5.25Zm3.75 8.25v-3a3.75 3.75 0 1 0-7.5 0v3h7.5Z"
                  clipRule="evenodd"
                />
              </svg>
              <p className="text-xs text-base-content/50 leading-relaxed">
                Ce document est protégé et lié au dossier médical du patient sur la plateforme
                AGBAZA. Toute reproduction ou modification non autorisée est interdite.
              </p>
            </div>
          </div>
        </div>

        {/* Pied de page */}
        <p className="text-xs text-center opacity-40">
          Plateforme AGBAZA — Système national de santé numérique
        </p>
      </div>
    </div>
  );
}
